import gsap from "../gsap";

/**
 * Before/After Modal Module
 *
 * Opens a modal with swipeable before/after image pairs.
 * Supports touch, mouse drag, arrow keys, and button navigation.
 *
 * Usage:
 * <div data-module="beforeaftermodal" data-project-id="123" data-project-title="Project Name">
 *   <img src="..." alt="Click to view before/after">
 * </div>
 *
 * Expected data structure (passed via data attributes or set programmatically):
 * - data-project-id: Unique project identifier
 * - data-project-title: Project name
 * - data-before-after: JSON array of image pairs [{ before: "url", after: "url" }, ...]
 */
export class BeforeAfterModal {
  #modal;
  #modalContainer;
  #modalTitle;
  #modalTrack;
  #modalViewport;
  #modalCurrent;
  #modalTotal;
  #prevBtn;
  #nextBtn;
  #closeBtn;
  #currentIndex = 0;
  #slides = [];
  #isDragging = false;
  #startX = 0;
  #currentX = 0;
  #translateX = 0;
  #slideWidth = 0;

  constructor(element) {
    this.element = element;

    // Get modal elements
    this.#modal = document.getElementById("beforeafter-modal");
    if (!this.#modal) {
      console.warn("BeforeAfterModal: modal container not found");
      return;
    }

    this.#modalContainer = this.#modal.querySelector("[data-modal-container]");
    this.#modalTitle = this.#modal.querySelector("[data-modal-title]");
    this.#modalTrack = this.#modal.querySelector("[data-modal-track]");
    this.#modalViewport = this.#modal.querySelector("[data-modal-viewport]");
    this.#modalCurrent = this.#modal.querySelector("[data-modal-current]");
    this.#modalTotal = this.#modal.querySelector("[data-modal-total]");
    this.#prevBtn = this.#modal.querySelector("[data-modal-prev]");
    this.#nextBtn = this.#modal.querySelector("[data-modal-next]");
    this.#closeBtn = this.#modal.querySelector("[data-modal-close]");

    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // Add click listener to trigger element
    this.element.addEventListener("click", this.handleClick);
  }

  handleClick(e) {
    e.preventDefault();

    // Get project data from element
    const projectTitle = this.element.dataset.projectTitle || "Project";
    const beforeAfterData = this.element.dataset.beforeAfter;

    if (!beforeAfterData) {
      console.warn("BeforeAfterModal: no before/after data found");
      return;
    }

    let imageData;
    try {
      imageData = JSON.parse(beforeAfterData);
    } catch (err) {
      console.warn("BeforeAfterModal: invalid JSON data", err);
      return;
    }

    if (!Array.isArray(imageData) || imageData.length === 0) {
      console.warn("BeforeAfterModal: no image pairs found");
      return;
    }

    this.openModal(projectTitle, imageData);
  }

  openModal(title, imageData) {
    // Set title
    this.#modalTitle.textContent = title;

    // Build slides
    this.#buildSlides(imageData);

    // Reset state
    this.#currentIndex = 0;
    this.#translateX = 0;
    this.#updateSlidePosition(false);
    this.#updateCounter();
    this.#updateButtons();

    // Show modal
    this.#modal.classList.remove("hidden");
    this.#modal.classList.add("flex");

    // Animate in
    gsap.fromTo(
      this.#modal,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: "power2.out" }
    );

    // Add event listeners
    this.#addEventListeners();

    // Lock body scroll
    document.body.style.overflow = "hidden";

    // Calculate slide width
    this.handleResize();
  }

  closeModal() {
    // Animate out
    gsap.to(this.#modal, {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        this.#modal.classList.add("hidden");
        this.#modal.classList.remove("flex");

        // Clear slides
        this.#modalTrack.innerHTML = "";
        this.#slides = [];
      },
    });

    // Remove event listeners
    this.#removeEventListeners();

    // Unlock body scroll
    document.body.style.overflow = "";
  }

  #buildSlides(imageData) {
    this.#modalTrack.innerHTML = "";
    this.#slides = imageData;

    imageData.forEach((pair, index) => {
      const slide = document.createElement("div");
      slide.className = "flex-shrink-0 w-full";
      slide.dataset.slideIndex = index;

      slide.innerHTML = `
        <div class="grid grid-rows-2 gap-4 max-h-[70vh] m:grid-rows-1 m:grid-cols-2 m:gap-8">
          <div class="flex flex-col items-center m:place-self-end">
            <span class="text-brand-blue text-sm mb-2">Before</span>
            <img
              src="${pair.before}"
              alt="Before"
              class="w-full h-auto object-contain max-h-[30vh] m:max-h-[60vh]"
            >
          </div>
          <div class="flex flex-col items-center m:place-self-start">
            <span class="text-brand-blue text-sm mb-2">After</span>
            <img
              src="${pair.after}"
              alt="After"
              class="w-full h-auto object-contain max-h-[30vh] m:max-h-[60vh]"
            >
          </div>
        </div>
      `;

      this.#modalTrack.appendChild(slide);
    });

    this.#modalTotal.textContent = imageData.length;
  }

  #updateSlidePosition(animate = true) {
    this.#translateX = -this.#currentIndex * this.#slideWidth;

    if (animate) {
      gsap.to(this.#modalTrack, {
        x: this.#translateX,
        duration: 0.4,
        ease: "power2.out",
      });
    } else {
      gsap.set(this.#modalTrack, { x: this.#translateX });
    }
  }

  #updateCounter() {
    this.#modalCurrent.textContent = this.#currentIndex + 1;
  }

  #updateButtons() {
    this.#prevBtn.disabled = this.#currentIndex === 0;
    this.#nextBtn.disabled = this.#currentIndex === this.#slides.length - 1;
  }

  handleClose() {
    this.closeModal();
  }

  handlePrev() {
    if (this.#currentIndex > 0) {
      this.#currentIndex--;
      this.#updateSlidePosition();
      this.#updateCounter();
      this.#updateButtons();
    }
  }

  handleNext() {
    if (this.#currentIndex < this.#slides.length - 1) {
      this.#currentIndex++;
      this.#updateSlidePosition();
      this.#updateCounter();
      this.#updateButtons();
    }
  }

  handleKeydown(e) {
    switch (e.key) {
      case "Escape":
        this.closeModal();
        break;
      case "ArrowLeft":
        this.handlePrev();
        break;
      case "ArrowRight":
        this.handleNext();
        break;
    }
  }

  handlePointerDown(e) {
    // Only handle left mouse button or touch
    if (e.button && e.button !== 0) return;

    this.#isDragging = true;
    this.#startX = e.clientX;
    this.#currentX = e.clientX;

    // Disable transition during drag
    this.#modalTrack.style.transition = "none";

    // Capture pointer
    this.#modalViewport.setPointerCapture(e.pointerId);
  }

  handlePointerMove(e) {
    if (!this.#isDragging) return;

    this.#currentX = e.clientX;
    const deltaX = this.#currentX - this.#startX;
    const newTranslateX = this.#translateX + deltaX;

    // Apply transform
    gsap.set(this.#modalTrack, { x: newTranslateX });
  }

  handlePointerUp(e) {
    if (!this.#isDragging) return;

    this.#isDragging = false;

    // Re-enable transition
    this.#modalTrack.style.transition = "";

    // Calculate delta
    const deltaX = this.#currentX - this.#startX;
    const threshold = this.#slideWidth * 0.2; // 20% of slide width

    // Determine direction
    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && this.#currentIndex > 0) {
        // Swipe right - go to previous
        this.#currentIndex--;
      } else if (deltaX < 0 && this.#currentIndex < this.#slides.length - 1) {
        // Swipe left - go to next
        this.#currentIndex++;
      }
    }

    // Snap to position
    this.#updateSlidePosition();
    this.#updateCounter();
    this.#updateButtons();

    // Release pointer capture
    if (this.#modalViewport.hasPointerCapture(e.pointerId)) {
      this.#modalViewport.releasePointerCapture(e.pointerId);
    }
  }

  handleResize() {
    this.#slideWidth = this.#modalViewport.offsetWidth;
    this.#updateSlidePosition(false);
  }

  #addEventListeners() {
    this.#closeBtn.addEventListener("click", this.handleClose);
    this.#prevBtn.addEventListener("click", this.handlePrev);
    this.#nextBtn.addEventListener("click", this.handleNext);
    document.addEventListener("keydown", this.handleKeydown);

    // Pointer events for swipe
    this.#modalViewport.addEventListener("pointerdown", this.handlePointerDown);
    this.#modalViewport.addEventListener("pointermove", this.handlePointerMove);
    this.#modalViewport.addEventListener("pointerup", this.handlePointerUp);
    this.#modalViewport.addEventListener("pointercancel", this.handlePointerUp);

    // Resize
    window.addEventListener("resize", this.handleResize);

    // Prevent text selection during drag
    this.#modalViewport.style.userSelect = "none";
    this.#modalViewport.style.touchAction = "pan-y pinch-zoom";
  }

  #removeEventListeners() {
    this.#closeBtn.removeEventListener("click", this.handleClose);
    this.#prevBtn.removeEventListener("click", this.handlePrev);
    this.#nextBtn.removeEventListener("click", this.handleNext);
    document.removeEventListener("keydown", this.handleKeydown);

    this.#modalViewport.removeEventListener("pointerdown", this.handlePointerDown);
    this.#modalViewport.removeEventListener("pointermove", this.handlePointerMove);
    this.#modalViewport.removeEventListener("pointerup", this.handlePointerUp);
    this.#modalViewport.removeEventListener("pointercancel", this.handlePointerUp);

    window.removeEventListener("resize", this.handleResize);
  }

  transitionOut() {
    this.destroy();
  }

  destroy() {
    // Close modal if open
    if (!this.#modal.classList.contains("hidden")) {
      this.closeModal();
    }

    // Remove click listener from trigger element
    this.element.removeEventListener("click", this.handleClick);
  }
}
